import type React from "react";
import { useParams } from "react-router-dom";
import { useChannel } from "@/hooks/channel/useChannel";
import { usePosts } from "@/hooks/channel/usePosts";
import { PostForm } from "./components/PostForm/PostForm";
import { PostList } from "./components/PostList/PostList";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ChannelDetailPage: React.FC = () => {
	const { channelId } = useParams<{ channelId: string }>();
	const {
		channel,
		loading: channelLoading,
		error: channelError,
	} = useChannel(channelId);
	const {
		posts,
		loading: postsLoading,
		error: postsError,
	} = usePosts(channelId);

	if (channelLoading || postsLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-12 w-1/3" />
				<Skeleton className="h-24 w-full" />
				<div className="space-y-4">
					{Array.from({ length: 3 }, () => crypto.randomUUID()).map((id) => (
						<Skeleton key={id} className="h-32 w-full" />
					))}
				</div>
			</div>
		);
	}

	if (channelError || postsError) {
		return (
			<Alert variant="destructive">
				<AlertDescription>
					チャンネルの読み込みに失敗しました。
				</AlertDescription>
			</Alert>
		);
	}

	if (!channel || !channelId) {
		return (
			<Alert variant="destructive">
				<AlertDescription>チャンネルが見つかりませんでした。</AlertDescription>
			</Alert>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center space-x-3">
				<span className="text-3xl">{channel?.emoji}</span>
				<div>
					<h1 className="text-3xl font-bold">{channel?.name}</h1>
					<p className="text-gray-600">{channel?.description}</p>
				</div>
			</div>

			{channel?.type === "TEXT" && <PostForm channelId={channelId} />}

			<PostList posts={posts} />
		</div>
	);
};

export default ChannelDetailPage;
